import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LocalizedLink } from '@rsc/gatsby-localization-helpers';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation } from 'react-use';
import { ProductContainerContext } from '@rsc/contexts/ProductContainerContext';
import { CartContext } from '@rsc/contexts/CartContext';
import AddToCartWithQuantity from '@rsc/ui-components/tailwind/AddToCartWithQuantity';
import OutOfStock from '@rsc/ui-components/tailwind/OutOfStock';
import ProductMessaging from '@rsc/ui-components/tailwind/ProductMessaging';
import ProductSellingPlans from '@rsc/ui-components/tailwind/ProductSellingPlans';
import ColorSwatch from '@rsc/ui-components/tailwind/ColorSwatch';
import ProductAddOn from '@rsc/ui-components/tailwind/ProductAddOn';
import Price from '@rsc/ui-components/tailwind/Price';
import { Label } from '@rsc/ui-components/shadcn/Label';
import OptionTag from '@rsc/ui-components/tailwind/OptionTag';
import type { SanitySiteSettings } from '@rsc/models/gatsby-graphql-types';
import { filteredVariants, pushToDataLayer } from '@rsc/utils';
import { cn } from '@rsc/tailwind-base/utils';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { isPixelSupported, pixelCategoryFromLocation, PIXELS } from '@rsc/utils/pixels.ts';

interface Props {
  siteSettings?: SanitySiteSettings;
  showPrice?: boolean;
  formClasses?: string;
  buttonFull?: boolean;
  onClose?: () => void;
}

export default function ProductOptions({
  buttonFull = true,
  onClose = null,
  showPrice = false,
  siteSettings = null,
  formClasses,
}: Readonly<Props>) {
  const location = useLocation();
  const {
    activeVariant,
    addOns,
    product,
    handleOptionChange,
    selectedOptions,
    subscribed,
    sellingPlanGroups,
    productOptions,
    variants,
    productLifecycle,
    quantity,
    pricing,
  } = useContext(ProductContainerContext) as any;
  const { tt } = useUiComponentsInternationalContext();

  // @ts-expect-error until context is converted to TS...KA
  const { addToCart } = useContext(CartContext);
  const [showOutOfStock, setShowOutOfStock] = useState(null);

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          isSubscription: yup.bool().required(),
          sellingPlanId: yup.string().when('isSubscription', {
            is: value => value === true,
            then: yup
              .string()
              .required(
                tt(s => s.productOptionsComponent.selectAFrequencyErrorMessage)
              ),
            otherwise: yup.string().strip(), // remove field from output for one-time orders
          }),
        })
        .required(),
    [tt]
  );

  type FormData = yup.InferType<typeof validationSchema>;

  const formMethods = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: { isSubscription: subscribed, sellingPlanId: '' },
  });

  useEffect(() => {
    formMethods.setValue('isSubscription', subscribed);
  }, [formMethods, subscribed]);

  useEffect(() => {
    setShowOutOfStock(
      !activeVariant?.availableForSale &&
        activeVariant?.variantLifecycle === 'Available' &&
        activeVariant?.quantityAvailable <= 0
    );
  }, [
    activeVariant?.availableForSale,
    activeVariant?.quantityAvailable,
    activeVariant?.variantLifecycle,
  ]);

  const onSubmit: SubmitHandler<FormData> = async (data, e) => {
    e.preventDefault();
    // ! addToCart expects sellingPlanGroup object
    const plan = sellingPlanGroups?.find(p => p.id === data.sellingPlanId);

    await addToCart({
      activeVariant,
      addOns,
      product,
      quantity,
      selectedSellingPlan: plan,
      variantId: activeVariant.shopifyId,
      variantMetafields: activeVariant?.metafields,
    });

    // for closing modals other functions etc...
    if (typeof onClose === 'function') onClose();

    if (isPixelSupported(PIXELS.gtm)) {
      const mappedAddonProducts =
        addOns?.map(s => ({
          quantity: s.cartProps.quantity,
          variant: {
            ...s,
            title: s.cartProps.variantTitle,
          },
          product: {
            title: s.cartProps.title,
            productType: s.cartProps.category,
            vendor: s.cartProps.brand,
          },
          isSubscription: !!plan,
          category: pixelCategoryFromLocation(location),
        })) || [];

      const GAItems = [
        {
          variant: {
            ...activeVariant,
            price: plan ? plan.price : activeVariant.price,
          },
          product: product.shopifyProduct,
          quantity,
          isSubscription: !!plan,
          category: pixelCategoryFromLocation(location)
        },
        ...mappedAddonProducts,
      ];

      pushToDataLayer('add_to_cart', {
        ecommerce: {
          currency: 'USD',
          value: GAItems.reduce((total, GAItem) => {
            if (GAItem.isSubscription) {
              return total + GAItem.quantity * Number(plan.price);
            }
            return total + GAItem.quantity * GAItem.variant.price;
          }, 0.0),
        },
        items: GAItems,
      });
    }
  };

  const updatedOptions = productOptions
    ?.flatMap(po => po.values.map(value => ({ value, name: po.name })))
    ?.map(option => {
      let unavailable = false;

      // Check if the option is selected and available...KA
      const isSelectedOption = option.value === selectedOptions[option.name];

      const selectedOptionAvailable = variants.some(variant =>
        Object.entries(selectedOptions).every(([n, v]) => {
          const relatedOption = variant.variantOptions?.find(o => o.name === n);
          return (
            !!relatedOption &&
            relatedOption.value === v &&
            variant.availableForSale &&
            variant.quantityAvailable > 0
          );
        })
      );

      if (isSelectedOption && !selectedOptionAvailable) {
        unavailable = true;
      }

      // If option is not selected, check whether any combination of the option is in stock...KA
      if (!isSelectedOption && Object.keys(selectedOptions).length) {
        const unselectedOptions = {
          ...selectedOptions,
          [option.name]: option.value,
        };

        const unselectedOptionAvailable = variants.some(variant =>
          Object.entries(unselectedOptions).every(([n, v]) => {
            const relatedOption = variant.variantOptions?.find(
              o => o.name === n
            );

            return (
              !!relatedOption &&
              relatedOption.value === v &&
              variant.availableForSale &&
              variant.quantityAvailable > 0
            );
          })
        );
        if (!unselectedOptionAvailable) unavailable = true;
      }

      return {
        ...option,
        unavailable,
      };
    });

  const renderColorOption = opt => {
    const colorOption = opt;

    let colorOptions = [];

    if (colorOption?.values?.length > 1) {
      colorOptions = colorOption.values.map(cov => {
        const shopifyVariant = filteredVariants(
          product.shopifyProduct?.variants
        )?.find(v =>
          v.selectedOptions.some(
            so => so.name.toLowerCase().includes('color') && so.value === cov
          )
        );

        const sanityVariant = product.store?.variants?.find(
          v => v?.sku === shopifyVariant?.sku
        );

        if (sanityVariant) {
          const image = sanityVariant?.metafields.find(mf =>
            ['swatch_image', 'swatch_hex'].includes(mf.name)
          );

          if (image) {
            return {
              image: image.value,
              type: image.name,
              value: cov,
              name: colorOption.name,
            };
          }
        }

        return {
          value: cov,
          type: 'string',
          name: colorOption.name,
        };
      });

      return (
        colorOption &&
        colorOptions.length > 1 && (
          <div className="flex flex-wrap gap-3">
            {colorOptions.map(v => {
              const unavailable = updatedOptions.some(
                uo =>
                  uo.name === v.name && uo.value === v.value && uo.unavailable
              );

              if (['swatch_image', 'swatch_hex'].includes(v.type)) {
                return (
                  <ColorSwatch
                    handleChange={handleOptionChange}
                    key={v.value}
                    option={v}
                    selected={selectedOptions[v.name] === v.value}
                    unavailable={unavailable}
                  />
                );
              }

              return (
                <OptionTag
                  key={v.value}
                  option={colorOption}
                  value={v.value}
                  selectedOptions={selectedOptions}
                  handleOptionChange={handleOptionChange}
                  unavailable={unavailable}
                />
              );
            })}
          </div>
        )
      );
    }
    return null;
  };

  function renderDefaultOption(opt) {
    return opt.values.map(v => {
      const unavailable = updatedOptions.some(
        uo => uo.name === opt.name && uo.value === v && uo.unavailable
      );

      return (
        <OptionTag
          key={v}
          option={opt}
          value={v}
          selectedOptions={selectedOptions}
          handleOptionChange={handleOptionChange}
          unavailable={unavailable}
        />
      );
    });
  }

  // handles out of stock form
  const shopifyId = activeVariant?.shopifyId;
  const variantIdNumber = shopifyId?.substring(shopifyId.lastIndexOf('/') + 1);
  const oosKey = `oos-${variantIdNumber}`;
  if (typeof window !== 'undefined') {
    const oosLocalStorage = window.localStorage.getItem(oosKey);
    if (activeVariant?.availableForSale && oosLocalStorage !== null) {
      window.localStorage.removeItem(oosKey);
    }
  }

  return (
    <FormProvider {...formMethods}>
      <div className="flex flex-col items-start gap-8">
        <form
          onSubmit={formMethods.handleSubmit(onSubmit)}
          className={cn('flex flex-col items-start gap-6', formClasses)}
        >
          <ProductSellingPlans />

          {productOptions?.map(opt => {
            const lowerCaseName = opt.name?.toLowerCase();
            const legendId = `legend-${lowerCaseName}`;

            return (
              <fieldset
                key={lowerCaseName}
                className="flex flex-col self-stretch items-start gap-2"
              >
                <Label
                  id={legendId}
                  className="flex items-start gap-2 self-stretch"
                >
                  {opt?.name}
                  {product.displaySizingChart &&
                    product.sizingChartUrl &&
                    lowerCaseName === 'size' && (
                      <LocalizedLink
                        className="underline hover:no-underline text-foreground-link"
                        to={product.sizingChartUrl}
                      >
                        {tt(s => s.productOptionsComponent.viewSizingChartLink)}
                      </LocalizedLink>
                    )}
                </Label>

                <div
                  aria-labelledby={legendId}
                  role="radiogroup"
                  className="flex flex-wrap gap-3"
                >
                  {lowerCaseName?.includes('color')
                    ? renderColorOption(opt)
                    : renderDefaultOption(opt)}
                </div>
              </fieldset>
            );
          })}

          {showPrice &&
            activeVariant?.variantLifecycle !== 'Obsolete' &&
            productLifecycle !== 'Obsolete' && (
              <Price
                product={product}
                pricing={pricing}
                subscribed={subscribed}
              />
            )}
          {!showOutOfStock && <AddToCartWithQuantity buttonFull={buttonFull} />}
        </form>

        {showOutOfStock && (
          <OutOfStock variantIdNumber={variantIdNumber} oosKey={oosKey} />
        )}

        {siteSettings && (
          <ProductMessaging
            shipping={siteSettings?.shipping}
            returnDays={siteSettings?.returnDays}
            deliveryTimeEstimate={siteSettings?.deliveryTimeEstimate}
          />
        )}
        <ProductAddOn />
      </div>
    </FormProvider>
  );
}
